import { oc } from "@orpc/contract";
import { z } from "zod";
import { SchemaFieldTypeSchema } from "./core/schema-definitions.contract";

const DataAssetVersionSchema = z.object({
  id: z.uuid(),
  dataAssetId: z.uuid(),
  versionNumber: z.number(),
  status: z.enum(["pending_upload", "ready", "error"]),
  reason: z.string().nullable().optional(),
  contentType: z.string(),
  containerName: z.string(),
  blobKey: z.string(),
  fileSize: z.number().nullable().optional(),
  fileName: z.string(),
  uploadedBy: z.uuid(),
  createdAt: z.iso.datetime(),
});

export type DataAssetVersionResponse = z.infer<typeof DataAssetVersionSchema>;

/** Enough of the attached schema definition to name it in the UI. */
const AttachedSchemaDefinitionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  version: z.number(),
});

const DataAssetSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  currentVersionId: z.uuid().nullable().optional(),
  createdBy: z.uuid(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  schemaDefinitionId: z.uuid().nullable().optional(),
  /**
   * The version that was checked when the schema was attached. When this stops
   * matching `currentVersionId`, a newer version has been uploaded that was
   * never validated.
   */
  schemaValidatedVersionId: z.uuid().nullable().optional(),
  schemaValidatedAt: z.iso.datetime().nullable().optional(),
  schemaDefinition: AttachedSchemaDefinitionSchema.nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  currentVersion: DataAssetVersionSchema.nullable().optional(),
});

const ValidationIssueSchema = z.object({
  /** 1-based record number, or null for file-level issues (headers, parse errors). */
  record: z.number().nullable(),
  field: z.string().nullable(),
  message: z.string(),
});

const ValidationReportSchema = z.object({
  conforms: z.boolean(),
  format: z.enum(["csv", "json"]),
  recordsChecked: z.number(),
  /** Total issues found, which can exceed `issues.length`. */
  issueCount: z.number(),
  issues: z.array(ValidationIssueSchema),
  /** True when the scan stopped early because too many issues were found. */
  truncated: z.boolean(),
  bytesRead: z.number(),
});

export type DataAssetValidationReportResponse = z.infer<
  typeof ValidationReportSchema
>;

/**
 * A field to validate against, for checks that run before a schema exists.
 * Only what the validator actually reads — nested container types are out of
 * scope here, since this path exists for flat CSV/JSON records.
 */
const ValidationFieldSchema = z.object({
  fieldName: z.string().min(1),
  // The real enum, so an unknown type is rejected at the boundary rather than
  // cast through into the validator.
  fieldType: SchemaFieldTypeSchema,
  isRequired: z.boolean(),
  isMultiple: z.boolean().optional(),
  validationRules: z
    .object({
      min: z.number().optional(),
      max: z.number().optional(),
      pattern: z.string().optional(),
      minLength: z.number().optional(),
      maxLength: z.number().optional(),
      precision: z.number().optional(),
      scale: z.number().optional(),
    })
    .nullable()
    .optional(),
  enumValues: z.array(z.string()).nullable().optional(),
});

export type DataAssetValidationFieldInput = z.infer<
  typeof ValidationFieldSchema
>;

export type DataAssetResponse = z.infer<typeof DataAssetSchema>;

const DataAssetWithVersionsSchema = DataAssetSchema.extend({
  versions: z.array(DataAssetVersionSchema),
});

export type DataAssetWithVersionsResponse = z.infer<
  typeof DataAssetWithVersionsSchema
>;

export const dataAssetContracts = {
  create: oc
    .input(
      z.object({
        name: z.string().min(1).max(255),
        description: z.string().optional(),
        fileName: z.string().min(1),
        contentType: z.string().min(1),
      }),
    )
    .output(
      z.object({
        dataAsset: DataAssetSchema,
        version: DataAssetVersionSchema,
        uploadUrl: z.string(),
      }),
    )
    .route({ method: "POST" }),

  confirmUpload: oc
    .input(
      z.object({
        versionId: z.uuid(),
        fileSize: z.number().optional(),
      }),
    )
    .output(
      z.object({
        dataAsset: DataAssetSchema,
        version: DataAssetVersionSchema,
      }),
    )
    .route({ method: "POST" }),

  list: oc
    .input(z.object({}))
    .output(z.object({ dataAssets: z.array(DataAssetSchema) }))
    .route({ method: "GET" }),

  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(
      z.object({
        dataAsset: DataAssetWithVersionsSchema,
      }),
    )
    .route({ method: "GET" }),

  update: oc
    .input(
      z.object({
        id: z.uuid(),
        name: z.string().min(1).max(255).optional(),
        description: z.string().nullable().optional(),
      }),
    )
    .output(z.object({ dataAsset: DataAssetSchema }))
    .route({ method: "PUT" }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),

  uploadVersion: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        fileName: z.string().min(1),
        contentType: z.string().min(1),
        reason: z.string().optional(),
      }),
    )
    .output(
      z.object({
        version: DataAssetVersionSchema,
        uploadUrl: z.string(),
      }),
    )
    .route({ method: "POST" }),

  confirmVersionUpload: oc
    .input(
      z.object({
        versionId: z.uuid(),
        fileSize: z.number().optional(),
      }),
    )
    .output(
      z.object({
        dataAsset: DataAssetSchema,
        version: DataAssetVersionSchema,
      }),
    )
    .route({ method: "POST" }),

  revertVersion: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        versionId: z.uuid(),
      }),
    )
    .output(z.object({ dataAsset: DataAssetSchema }))
    .route({ method: "POST" }),

  getVersionSignedUrl: oc
    .input(z.object({ versionId: z.uuid() }))
    .output(z.object({ downloadUrl: z.string() }))
    .route({ method: "GET" }),

  /**
   * Validates the asset's current contents against a schema definition and, if
   * every record conforms, records the attachment. Pass `dryRun` to get the
   * report without changing anything.
   */
  attachSchema: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        schemaDefinitionId: z.uuid(),
        dryRun: z.boolean().optional(),
      }),
    )
    .output(
      z.object({
        attached: z.boolean(),
        report: ValidationReportSchema,
        /** Present only when the schema was actually attached. */
        dataAsset: DataAssetSchema.nullable(),
      }),
    )
    .route({ method: "POST" }),

  detachSchema: oc
    .input(z.object({ dataAssetId: z.uuid() }))
    .output(z.object({ dataAsset: DataAssetSchema }))
    .route({ method: "POST" }),

  /**
   * Checks the asset against a field list that hasn't been saved as a schema,
   * so a suggested schema can be proven against the file before the user is
   * asked to keep it.
   */
  validateFields: oc
    .input(
      z.object({
        dataAssetId: z.uuid(),
        fields: z.array(ValidationFieldSchema).min(1),
      }),
    )
    .output(z.object({ report: ValidationReportSchema }))
    .route({ method: "POST" }),
} as const;
